import type { GridSortModel } from '@mui/x-data-grid-premium';
import type {
  AdvisorAggregatedHolding,
  AdvisorAggregatedHoldingHeldBy,
  AdvisorAggregatedHoldingsSort,
} from '@/store/advisorAggregatedHoldingsApi';

/** Grid row: the API record plus a synthetic, guaranteed-unique `id`. */
export type AggregatedHoldingRow = AdvisorAggregatedHolding & { id: string };

/**
 * Grid field -> API sort `Property`. Sorting is server-side, so the property
 * sent must be one the backend understands: the column's field is
 * `CUSIPDisplay`, the name the grid config ships, but it sorts on `CUSIP`.
 * `HeldBy` is absent because it is a client-derived label the backend cannot
 * sort (legacy marks it unsortable too).
 */
const SORT_PROPERTY_BY_FIELD: Record<string, string> = {
  Name: 'Name',
  Symbol: 'Symbol',
  CUSIPDisplay: 'CUSIP',
  Balance: 'Balance',
  PercentHome: 'PercentHome',
};

/** Translates the grid's sort model into the API's `sort[]` shape. */
export function toApiSort(
  model: GridSortModel,
): AdvisorAggregatedHoldingsSort[] {
  return model.flatMap((item, index) => {
    const property = SORT_PROPERTY_BY_FIELD[item.field];
    if (!property || !item.sort) return [];
    return [{ property, direction: item.sort, order: index }];
  });
}

/**
 * "Held By" cell text: a formatted client count, or empty when there are no
 * holders. `formatCount` is injected so this stays pure and testable without i18n.
 */
export function heldByLabel(
  heldBy: AdvisorAggregatedHoldingHeldBy[] | undefined,
  formatCount: (count: number) => string,
): string {
  if (!heldBy?.length) return '';
  return formatCount(heldBy.length);
}

/**
 * Builds rows with collision-proof ids.
 *
 * `Key` is NOT unique. The backend groups by CUSIP, but when the firm enables
 * `ShowPrimaryAdvisor` it groups by `Cusip + ":" + PrimaryAdvisorId` while still
 * setting `Key = first.Cusip`, so the same `Key` can appear on several rows. It
 * can also be a synthetic id, or empty. Duplicate ids make MUI silently drop
 * rows, so disambiguate by primary advisor and then by occurrence.
 */
export function buildAggregatedHoldingRows(
  records: readonly AdvisorAggregatedHolding[],
): AggregatedHoldingRow[] {
  const seen = new Set<string>();

  return records.map((record, index) => {
    const base = record.Key
      ? `agg-${record.Key}-${String(record.PrimaryAdvisorId ?? 'na')}`
      : `agg-idx-${String(index)}`;

    let id = base;
    let suffix = 2;
    while (seen.has(id)) {
      id = `${base}-${String(suffix)}`;
      suffix += 1;
    }
    seen.add(id);

    return { ...record, id };
  });
}

/**
 * `PercentHome` already arrives scaled as a percentage (100 means 100%), so it
 * must NOT go through `convertNumberToPercentage` — that applies Intl's
 * `style: 'percent'`, which multiplies by 100 and would render "10,000.00%".
 *
 * The field is declared non-optional, but this runs as a grid `valueFormatter`
 * over a paginated response, so a missing cell arrives as null/undefined and
 * `NaN` must render blank rather than "NaN%".
 */
export function formatAggregatedPercent(
  value: number | null | undefined,
): string {
  return value != null && Number.isFinite(value) ? `${value.toFixed(2)}%` : '';
}
